// Сквозные метки (B, docs/product/engineering-plan.md): откуда пришёл будущий ученик. При загрузке
// приложения (main.tsx, до роутера: редирект гостя на /auth уносит метки из адреса) запоминаем
// первую точку с метками и контекст визита; при регистрации отдаём их бэкенду — `attribution`
// в теле /auth/registration или query входа через Яндекс — и стираем.
//
// localStorage `lx.attribution.v1` (регистрация бывает через дни после первого визита):
//   first          — первый визит: время, путь входа, внешний referrer, визит лендинга;
//   first_labeled  — первый визит с метками, не перезаписывается 90 дней.
// Всё старше 90 дней забывается. Нет меток в адресе — источник из cookie `lomora.visit`, которую
// ставит лендинг на .lomora.ru ({src: метки, ref: внешний хост, lp: путь на лендинге, t}).
// `utm_source=landing` — внутренняя метка ссылок лендинга (landing/lib/links.ts), а не канал:
// она не мешает записать настоящие метки, пришедшие позже. От referrer храним только хост;
// токены и прочие параметры адреса не читаются.
//
// Модуль без импортов приложения: его проверяет tests/attribution.test.ts под Node.

export const ATTRIBUTION_KEY = 'lx.attribution.v1';
export const ATTRIBUTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
const VISIT_COOKIE = 'lomora.visit';

/** Метки визита. `lp` — место клика на лендинге, когда utm_content занят креативом объявления. */
const LABEL_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'yclid',
  'gclid',
  'lp',
] as const;
const VALUE_MAX = 200;
const REF_MAX = 64;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Страницы модуля: /m/:uuid (публичная ссылка, C) и /library/:uuid. */
const ENTRY_MODULE_PATH = /^\/(?:m|library)\/([^/]+)\/?$/i;
const HOST = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/;

type LabelKey = (typeof LABEL_KEYS)[number];
type Labels = Partial<Record<LabelKey, string>>;

interface Touch {
  at: number;
  labels: Labels | null;
  entry_path: string | null;
  referrer_host: string | null;
  landing_path: string | null;
  landing_referrer_host: string | null;
  ref: string | null;
  entry_module_id: string | null;
}

interface StoredAttribution {
  first: Touch;
  first_labeled: Touch | null;
}

/** Поле `attribution` регистрации: snake_case, пустые поля не передаются. */
export interface SignupAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  yclid?: string;
  gclid?: string;
  lp?: string;
  ref?: string;
  entry_module_id?: string;
  entry_path?: string;
  referrer_host?: string;
  landing_path?: string;
  landing_referrer_host?: string;
  first_seen_at?: string;
}

export interface CaptureInput {
  search: string;
  pathname: string;
  referrer: string;
  cookie: string;
  /** Хост приложения: переход с него самого — не внешний referrer. */
  host: string;
  now: number;
}

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function localStore(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // хранилище заблокировано настройками приватности
  }
}

/** Строка без управляющих символов, не длиннее max символов (по кодовым точкам: эмодзи не
 *  разрезается пополам); пустая → null. */
function clean(value: unknown, max = VALUE_MAX): string | null {
  if (typeof value !== 'string') return null;
  const text = Array.from(value.replace(/[\u0000-\u001f\u007f]/g, '').trim())
    .slice(0, max)
    .join('')
    .trim();
  return text || null;
}

function cleanHost(value: unknown): string | null {
  const host = clean(value)?.toLowerCase().replace(/:\d+$/, '') ?? null;
  return host && HOST.test(host) ? host : null;
}

/** Только путь: без query и hash (там бывают секреты), от корня. */
function cleanPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const path = clean(value.split(/[?#]/)[0]);
  return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
}

function cleanModuleId(value: unknown): string | null {
  return typeof value === 'string' && UUID.test(value) ? value.toLowerCase() : null;
}

function labelsFrom(read: (key: LabelKey) => unknown): Labels | null {
  const labels: Labels = {};
  for (const key of LABEL_KEYS) {
    const value = clean(read(key));
    if (value) labels[key] = value;
  }
  return Object.keys(labels).length > 0 ? labels : null;
}

const isInternal = (labels: Labels) => labels.utm_source?.toLowerCase() === 'landing';

function parseCookieJson(raw: string): unknown {
  const decoders = [() => decodeURIComponent(raw), () => raw, () => atob(raw.replace(/-/g, '+').replace(/_/g, '/'))];
  for (const decode of decoders) {
    try {
      return JSON.parse(decode());
    } catch {
      // следующий вариант кодирования
    }
  }
  return null;
}

interface VisitCookie {
  src: Labels | null;
  ref: string | null;
  lp: string | null;
}

/** Cookie визита лендинга `lomora.visit`; null — нет или не разобрать. */
function readVisitCookie(cookie: string): VisitCookie | null {
  const entry = cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${VISIT_COOKIE}=`));
  if (!entry) return null;
  const data = parseCookieJson(entry.slice(VISIT_COOKIE.length + 1));
  if (!data || typeof data !== 'object') return null;
  const visit = data as { src?: unknown; ref?: unknown; lp?: unknown };
  const src = visit.src && typeof visit.src === 'object' ? (visit.src as Record<string, unknown>) : null;
  return {
    src: src ? labelsFrom((key) => src[key]) : null,
    ref: cleanHost(visit.ref),
    lp: cleanPath(visit.lp),
  };
}

function referrerHost(referrer: string, selfHost: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host && host !== selfHost.toLowerCase() ? cleanHost(host) : null;
  } catch {
    return null;
  }
}

function touchFrom(input: CaptureInput): Touch {
  const params = new URLSearchParams(input.search);
  const visit = readVisitCookie(input.cookie);
  const fromUrl = labelsFrom((key) => params.get(key));
  const fromVisit = visit?.src && !isInternal(visit.src) ? visit.src : null;

  let labels: Labels | null;
  if (fromUrl && !isInternal(fromUrl)) {
    labels = { ...fromUrl };
    // yclid и gclid лендинг в адрес приложения не переносит (решение B) — берём их из визита,
    // если это тот же источник.
    if (fromVisit && fromVisit.utm_source === fromUrl.utm_source) {
      if (!labels.yclid && fromVisit.yclid) labels.yclid = fromVisit.yclid;
      if (!labels.gclid && fromVisit.gclid) labels.gclid = fromVisit.gclid;
    }
  } else {
    // Ссылка лендинга без подмены (utm_source=landing) или без меток — источник из визита лендинга.
    labels = fromVisit ?? fromUrl;
  }

  const moduleMatch = ENTRY_MODULE_PATH.exec(input.pathname);
  return {
    at: input.now,
    labels,
    entry_path: cleanPath(input.pathname),
    referrer_host: referrerHost(input.referrer, input.host),
    landing_path: visit?.lp ?? null,
    landing_referrer_host: visit?.ref ?? null,
    ref: clean(params.get('ref'), REF_MAX),
    entry_module_id: cleanModuleId(moduleMatch?.[1]),
  };
}

/** Запись из хранилища заново проверяется: её могли подменить или оставить старой версией. */
function toTouch(value: unknown): Touch | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const at = Number(raw.at);
  if (!Number.isFinite(at)) return null;
  const labels = raw.labels && typeof raw.labels === 'object' ? (raw.labels as Record<string, unknown>) : null;
  return {
    at,
    labels: labels ? labelsFrom((key) => labels[key]) : null,
    entry_path: cleanPath(raw.entry_path),
    referrer_host: cleanHost(raw.referrer_host),
    landing_path: cleanPath(raw.landing_path),
    landing_referrer_host: cleanHost(raw.landing_referrer_host),
    ref: clean(raw.ref, REF_MAX),
    entry_module_id: cleanModuleId(raw.entry_module_id),
  };
}

function load(store: KeyValueStore, now: number): StoredAttribution | null {
  let raw: string | null;
  try {
    raw = store.getItem(ATTRIBUTION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let data: { first?: unknown; first_labeled?: unknown };
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  // Будущее время (переведённые часы) — тоже не доверяем.
  const fresh = (touch: Touch | null) =>
    touch && now - touch.at <= ATTRIBUTION_WINDOW_MS && touch.at <= now + 60_000 ? touch : null;
  const labeled = fresh(toTouch(data.first_labeled));
  const first = fresh(toTouch(data.first)) ?? labeled;
  return first ? { first, first_labeled: labeled?.labels ? labeled : null } : null;
}

function save(store: KeyValueStore, value: StoredAttribution): void {
  try {
    store.setItem(ATTRIBUTION_KEY, JSON.stringify(value));
  } catch {
    // переполнено или заблокировано — без атрибуции регистрация работает как раньше
  }
}

function currentInput(): CaptureInput | null {
  try {
    return {
      search: window.location.search,
      pathname: window.location.pathname,
      referrer: document.referrer,
      cookie: document.cookie,
      host: window.location.hostname,
      now: Date.now(),
    };
  } catch {
    return null; // нет DOM или cookie недоступны (sandbox)
  }
}

/** Запомнить источник визита. Вызывать при старте приложения, до первого редиректа. */
export function captureAttribution(input: CaptureInput | null = currentInput(), store = localStore()): void {
  if (!input || !store) return;
  const touch = touchFrom(input);
  const stored = load(store, input.now);
  if (!stored) {
    save(store, { first: touch, first_labeled: touch.labels ? touch : null });
    return;
  }
  const current = stored.first_labeled?.labels ?? null;
  // Первая точка с метками держится 90 дней; внутреннюю метку лендинга вытесняет настоящая.
  const replace = !!touch.labels && (!current || (isInternal(current) && !isInternal(touch.labels)));
  save(store, replace ? { ...stored, first_labeled: touch } : stored);
}

/** Атрибуция для регистрации (snake_case) или null, если ничего не запомнено. */
export function readAttribution(now = Date.now(), store = localStore()): SignupAttribution | null {
  const stored = store ? load(store, now) : null;
  if (!stored) return null;
  const touch = stored.first_labeled ?? stored.first;
  const result: SignupAttribution = { ...touch.labels };
  if (touch.ref) result.ref = touch.ref;
  if (touch.entry_module_id) result.entry_module_id = touch.entry_module_id;
  if (touch.entry_path) result.entry_path = touch.entry_path;
  if (touch.referrer_host) result.referrer_host = touch.referrer_host;
  if (touch.landing_path) result.landing_path = touch.landing_path;
  if (touch.landing_referrer_host) result.landing_referrer_host = touch.landing_referrer_host;
  result.first_seen_at = new Date(stored.first.at).toISOString();
  return result;
}

/** Query для входа через Яндекс («?utm_source=vk&…»): бэкенд держит метки на время OAuth. */
export function attributionSearch(now = Date.now(), store = localStore()): string {
  const attribution = readAttribution(now, store);
  if (!attribution) return '';
  const params = new URLSearchParams();
  Object.entries(attribution).forEach(([key, value]) => {
    if (typeof value === 'string' && value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * Страница лендинга, с которой пришёл посетитель, — из cookie визита `lomora.visit` (живёт 30 мин с последнего
 * действия на лендинге). Атрибуцию регистрация уже стёрла, а знакомству (2026-11-02) нужно знать, шёл ли ученик за
 * карточками. Нет cookie или не разобрать — null.
 */
export function visitLandingPath(cookie: string | null = documentCookie()): string | null {
  return cookie ? (readVisitCookie(cookie)?.lp ?? null) : null;
}

function documentCookie(): string | null {
  try {
    return document.cookie;
  } catch {
    return null; // нет DOM или cookie недоступны (sandbox)
  }
}

/** Стереть после регистрации или входа: метки относятся к уже известному аккаунту. */
export function clearAttribution(store = localStore()): void {
  try {
    store?.removeItem(ATTRIBUTION_KEY);
  } catch {
    // заблокировано — запись истечёт сама через 90 дней
  }
}
